var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { when } from 'lit/directives/when.js';
import { provide } from '@lit/context';
import { AuthenticationState, authenticationContext, } from '@frontend_zz/web-component-contexts/Authentication/AuthenticationContext.js';
// Mixins
import { BaseElement } from '@frontend_zz/base-element/BaseElement.js';
// Utils
import { hasActiveItem } from './Navigation.utils.js';
// Components
import { NavigationItem } from './NavigationItem.js';
import { Surface } from '@frontend_zz/surface/Surface.js';
// Styling
import styles from './Navigation.css.js';
// Data
import content from './Navigation.content.json';
/**
 * @element zz-navigation
 */
export class Navigation extends BaseElement {
    constructor() {
        super(...arguments);
        /**
         * The maximum number of visible items
         */
        this.maxVisibleItems = 5;
        /**
         * The maximum number of visible items on mobile (this allows for hiding the menu on mobile for use with external triggers, like on the public website)
         */
        this.maxVisibleItemsMobile = 4;
        /**
         * The direction of the animation of the primary overflow menu
         */
        this.animationDirection = 'bottom-to-top';
        this.isAuthenticated = false;
        this.maxVisibleItemsCalculated = 0;
        this.authenticationContext = new AuthenticationState();
        this.moreSurrogateNavItemRef = createRef();
        this.menuRef = createRef();
        this.navItemRefs = new Set();
        this.updated = () => {
            const { authenticationContext, isAuthenticated } = this;
            authenticationContext.isAuthenticated = isAuthenticated;
            // Measure the height of the nav bar to position overflow menus above it, leaving enough space.
            // This component finishes rendering before its descendents so we have to wait a bit. Not sure if there is a good way handle this.
            setTimeout(() => {
                const navigationHeight = this.getBoundingClientRect().height;
                document.documentElement.style.setProperty('--bottom-navigation-height', `${navigationHeight}px`);
            }, 100);
        };
        /**
         *
         */
        this.handleResize = (_entries) => {
            const { menuRef } = this;
            if (!menuRef.value)
                return;
            const availableWidth = this.getBoundingClientRect().width;
            const moreWidth = 90; // It's hard to dynamically retrieve this value because we would need to actually render a situation with a 'more' menu item.
            const menuItems = menuRef.value.children;
            let itemCount = 0;
            let cumulativeWidth = 0;
            // Find the number of menu items we can show from the invisible, fully-rendered surrogate menu that fits the available space
            for (; itemCount < menuItems.length; itemCount++) {
                const nextItemWidth = menuItems[itemCount].getBoundingClientRect().width;
                const nextCumulativeWidth = cumulativeWidth + nextItemWidth;
                // If the next item would push the cumulative width over the available width, end the loop early
                if (nextCumulativeWidth > availableWidth) {
                    // No space for 'more' item ⇒ show one fewer item to make room
                    // This should work well without further calcs because the 'more' item is pretty much guaranteed to be smaller than actual menu items
                    if (availableWidth - cumulativeWidth < moreWidth) {
                        itemCount--;
                    }
                    break;
                }
                else {
                    cumulativeWidth += nextItemWidth;
                }
            }
            this.maxVisibleItemsCalculated = itemCount;
        };
        /**
         * Global click event handler
         */
        this.onClickGlobal = (event) => {
            const { closeAllOverflowMenus } = this;
            if (!event.target)
                return;
            const targetIsInNavigation = this.contains(event.target);
            const targetIsTrigger = event.target.closest('[data-navigation-trigger]');
            // On click outside, close any open overflow menus (soft dismiss)
            if (!(targetIsInNavigation || targetIsTrigger)) {
                closeAllOverflowMenus();
            }
        };
        /**
         * Toggle primary overflow menu ("more")
         */
        this.togglePrimaryOverflowMenu = () => {
            const { moreSurrogateNavItemRef, closeOtherOverflowMenus } = this;
            if (!moreSurrogateNavItemRef.value)
                return;
            closeOtherOverflowMenus(moreSurrogateNavItemRef.value);
            setTimeout(() => {
                if (!moreSurrogateNavItemRef.value)
                    return;
                moreSurrogateNavItemRef.value.isExpanded = !moreSurrogateNavItemRef.value.isExpanded;
            }, 0);
        };
        /**
         * Close all overflow menus
         */
        this.closeAllOverflowMenus = () => {
            const { navItemRefs } = this;
            [...navItemRefs].forEach((ref) => {
                if (!ref.value)
                    return;
                ref.value.isExpanded = false;
            });
        };
        /**
         * Close all overflow menus, except for the passed-in one
         * @param targetNavItem NavigationItem to not toggle off
         */
        this.closeOtherOverflowMenus = (targetNavItem) => {
            const { navItemRefs } = this;
            [...navItemRefs].forEach((otherNavItemRef) => {
                if (!(otherNavItemRef.value && targetNavItem))
                    return;
                if (otherNavItemRef.value === targetNavItem)
                    return;
                otherNavItemRef.value.isExpanded = false;
            });
            // Broadcast state of primary overflow menu
            this.dispatchEvent(new CustomEvent('navigation:toggle', {
                detail: { isExpanded: this.moreSurrogateNavItemRef.value?.isExpanded },
            }));
        };
        this.render = () => {
            const { data, maxVisibleItems, maxVisibleItemsMobile, maxVisibleItemsCalculated, animationDirection, moreSurrogateNavItemRef, menuRef, navItemRefs, closeOtherOverflowMenus, isMobile, isAuthenticated, } = this;
            const _data = typeof data === 'string' ? JSON.parse(data) : data;
            const effectiveMaxVisibleItems = isMobile && maxVisibleItemsMobile != null
                ? maxVisibleItemsMobile
                : Math.min(maxVisibleItems, maxVisibleItemsCalculated);
            const nonContextSwitchItems = _data.filter((item) => item.type !== 'context-switch');
            const contextSwitchItems = _data.filter((item) => item.type === 'context-switch');
            const visibleItems = nonContextSwitchItems.slice(0, effectiveMaxVisibleItems);
            const overflowItems = nonContextSwitchItems.slice(effectiveMaxVisibleItems);
            const onToggleExpanded = (event) => {
                closeOtherOverflowMenus(event.detail.element);
            };
            const renderMoreSurrogateNavItem = () => {
                const data = {
                    id: 'more',
                    label: content.overflowButtonLabel,
                    href: '#navigation-more-menu',
                    icon: 'more',
                    iconActive: 'moreFilled',
                    isActive: hasActiveItem(overflowItems),
                    subItems: isMobile ? [...overflowItems, ...contextSwitchItems] : overflowItems,
                };
                navItemRefs.add(moreSurrogateNavItemRef);
                return html `
                <zz-navigation-item
                    .data=${data}
                    level=${0}
                    animation-direction=${animationDirection}
                    ?only-render-overflow=${!visibleItems.length}
                    is-primary-overflow
                    @toggle-expanded=${onToggleExpanded}
                    ?is-authenticated=${isAuthenticated}
                    ${ref(moreSurrogateNavItemRef)}
                ></zz-navigation-item>
            `;
            };
            const renderNavigationItem = (item, level) => {
                const itemRef = createRef();
                navItemRefs.add(itemRef);
                // If this top-level item has an 'active' sub-item, also mark this item as 'active'
                item.isActive = hasActiveItem([item]);
                return html `
                <zz-navigation-item
                    class="nav-item"
                    .data=${item}
                    level=${level}
                    @toggle-expanded=${onToggleExpanded}
                    ${ref(itemRef)}
                ></zz-navigation-item>
            `;
            };
            return html `
            <nav class="navigation">
                <zz-surface class="wrapper" variant=${isMobile ? 'elevated-not-clickable-centered' : 'none'}>
                    <ul class="menu top-level">
                        ${repeat(visibleItems, (item) => renderNavigationItem(item, 1))}
                        ${when(overflowItems.length, renderMoreSurrogateNavItem)}
                    </ul>
                    <!-- Render an invisible surrogate copy with all main items for measuring -->
                    <ul ${ref(menuRef)} class="menu surrogate" role="presentation" aria-hidden="true">
                        ${repeat(nonContextSwitchItems, (item) => renderNavigationItem(item, 1))}
                    </ul>
                </zz-surface>
            </nav>
        `;
        };
    }
    static get styles() {
        return [styles];
    }
    static get scopedElements() {
        return {
            'zz-navigation-item': NavigationItem,
            'zz-surface': Surface,
        };
    }
    connectedCallback() {
        super.connectedCallback();
        const { togglePrimaryOverflowMenu, handleResize, onClickGlobal, closeAllOverflowMenus, parentElement } = this;
        window.addEventListener('navigation:toggle', togglePrimaryOverflowMenu);
        window.addEventListener('click', onClickGlobal);
        this.addEventListener('universalAnchorClick', closeAllOverflowMenus);
        if (parentElement) {
            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(parentElement);
        }
    }
    disconnectedCallback() {
        const { togglePrimaryOverflowMenu, onClickGlobal, closeAllOverflowMenus } = this;
        window.removeEventListener('navigation:toggle', togglePrimaryOverflowMenu);
        window.removeEventListener('click', onClickGlobal);
        this.removeEventListener('universalAnchorClick', closeAllOverflowMenus);
    }
}
__decorate([
    property()
], Navigation.prototype, "data", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: 'max-visible-items' })
], Navigation.prototype, "maxVisibleItems", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: 'max-visible-items-mobile' })
], Navigation.prototype, "maxVisibleItemsMobile", void 0);
__decorate([
    property({ attribute: 'animation-direction' })
], Navigation.prototype, "animationDirection", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'is-authenticated' })
], Navigation.prototype, "isAuthenticated", void 0);
__decorate([
    state()
], Navigation.prototype, "maxVisibleItemsCalculated", void 0);
__decorate([
    provide({ context: authenticationContext })
], Navigation.prototype, "authenticationContext", void 0);
